import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CTASection } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";

const principles = [
  { n: "01", t: "Governance-first", d: "Software should create accountability, not just record activity. Governance is our starting point, not an afterthought." },
  { n: "02", t: "Execution over storage", d: "A school is run in classrooms every day. We build for daily execution — not monthly reporting." },
  { n: "03", t: "One connected layer", d: "Every stakeholder belongs in one ecosystem — no scattered tools, no lost information." },
  { n: "04", t: "Infrastructure, not an app", d: "We are building the default operating layer schools depend on — quietly, reliably, at scale." },
];

export default function About() {
  return (
    <>
      <PageHero
        overline="About"
        title="Building the operating system for schools."
        sub="PARTECON is a technology company creating a new software category — an intelligent, governed operating layer that connects every stakeholder of a school on one live foundation."
      />

      {/* Vision + Mission — split */}
      <section className="canvas py-20 md:py-24" data-testid="vision-mission">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6 lg:gap-10">
          <Reveal>
            <div className="rounded-2xl bg-white border border-[#ECECE8] p-8 md:p-10 h-full" data-testid="vision-card">
              <span className="overline">Our vision</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-4 text-[#1A1D26] leading-tight">
                Every school, connected on one operating layer.
              </h2>
              <p className="text-[#6B7280] text-base leading-relaxed mt-5">
                A quiet, dependable infrastructure beneath every classroom, staffroom, dashboard and
                parent conversation — evolving continuously toward automation, intelligence and smart
                school infrastructure.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-[#0B1240] border border-white/10 p-8 md:p-10 h-full text-white" data-testid="mission-card">
              <span className="overline overline-light">Our mission</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-4 leading-tight">
                Replace disconnected school technology with one governed system.
              </h2>
              <p className="text-white/60 text-base leading-relaxed mt-5">
                Move schools from fragmented ERPs, messaging apps and spreadsheets to one operating
                layer — where every function, every stakeholder and every decision runs on the same
                live, governed foundation.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Operating philosophy */}
      <section className="chrome py-20 md:py-24" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="overline overline-light">Operating philosophy</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-semibold mt-3 max-w-2xl text-white">The principles behind PARTECON.</h2>
          </Reveal>
          <Stagger className="grid md:grid-cols-2 gap-4 mt-12">
            {principles.map((p) => (
              <StaggerItem key={p.n}>
                <div className="glass glass-hover rounded-2xl p-8 h-full" data-testid={`principle-${p.n}`}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-5xl font-light text-[#F1BF26]/60">{p.n}</span>
                    <h3 className="font-display text-xl font-semibold text-white">{p.t}</h3>
                  </div>
                  <p className="text-white/60 mt-4 leading-relaxed">{p.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Why we're building this */}
      <section className="canvas py-20 md:py-24" data-testid="why-building">
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <span className="overline">Why we're building this</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-4 leading-tight text-[#1A1D26]">
              Schools deserve infrastructure-grade software.
            </h2>
            <p className="text-[#6B7280] text-base md:text-lg leading-relaxed mt-6">
              For decades, school technology has meant records, reports and disconnected tools.
              PARTECON exists to change that — to give every modern school the same kind of connected,
              governed operating layer that every other serious institution runs on.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5 mt-9">
              <Link to="/platform" className="btn-primary px-7 py-3.5 text-sm inline-flex items-center gap-2" data-testid="about-platform-btn">
                See the platform <ArrowRight size={15} />
              </Link>
              <Link to="/how-it-works" className="text-[13.5px] font-semibold text-[#111A70] inline-flex items-center gap-1.5 hover:gap-2 transition-all" data-testid="about-how-btn">
                How PARTECON works <ArrowRight size={13} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

    </>
  );
}
